import React, { useState, useEffect } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboarding } from "@/context/OnboardingContext";
import { useEnhancedGeolocation } from "@/hooks/useEnhancedGeolocation";
import { useStepValidation } from "@/hooks/useStepValidation";
import { OnboardingErrorModal } from "./OnboardingErrorModal";
import { debugLogger } from '@/utils/debugLogger';
import CountrySelect from "./location/CountrySelect";
import StateSelect from "./location/StateSelect";
import CitySelect from "./location/CitySelect";
import LocationSuggestionBanner from "./location/LocationSuggestionBanner";
import StickyOnboardingFooter from "./StickyOnboardingFooter";

const GeolocationStep = () => {
  const { data, updateData, nextStep } = useOnboarding();
  const { validateAndSaveStep, isValidating } = useStepValidation();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    timestamp: string;
    step: number;
    type: string;
  } | null>(null);

  // Location fields
  const [selectedCountry, setSelectedCountry] = useState<string>(data?.location?.country || 'BR');
  const [selectedState, setSelectedState] = useState<string>(data?.location?.state || '');
  const [selectedCity, setSelectedCity] = useState<string>(data?.location?.city || '');
  
  // Automatic suggestion (using ISO codes)
  const [suggestedCity, setSuggestedCity] = useState<string>('');
  const [suggestedState, setSuggestedState] = useState<string>('');   // ISO code (ex: "SP")
  const [suggestedCountry, setSuggestedCountry] = useState<string>(''); // ISO code (ex: "BR")
  const [suggestionAccepted, setSuggestionAccepted] = useState(false);
  const [hasAttemptedAutoDetect, setHasAttemptedAutoDetect] = useState(false);

  const {
    isGettingLocation,
    getCurrentLocation,
    reset
  } = useEnhancedGeolocation();

  // Initialize with existing context data
  useEffect(() => {
    if (data?.location?.country) {
      setSelectedCountry(data.location.country);
    }
    if (data?.location?.state) {
      setSelectedState(data.location.state);
    }
    if (data?.location?.city) {
      setSelectedCity(data.location.city);
    }
  }, []);

  // Try auto-detect on mount (only as suggestion)
  useEffect(() => {
    if (!hasAttemptedAutoDetect && !selectedCity && !selectedState) {
      setHasAttemptedAutoDetect(true);
      handleAutoDetect(false);
    }
  }, [hasAttemptedAutoDetect, selectedCity, selectedState]);

  const handleAutoDetect = (forceUpdate = false) => {
    debugLogger.info('GeolocationStep: Starting auto-detection', { forceUpdate });
    reset();
    getCurrentLocation({
      onLocationObtained: async (locationData) => {
        debugLogger.debug('GeolocationStep: Auto-detected location:', locationData);
        
        const detectedCity = locationData.city || '';
        // Use stateCode (ISO code) instead of state (full name)
        const detectedState = locationData.stateCode || locationData.state || '';
        // Use countryCode from detection, not hardcoded
        const detectedCountry = locationData.countryCode || 'BR';
        
        debugLogger.debug('GeolocationStep: Parsed location', { 
          detectedCity, 
          detectedState, 
          detectedCountry 
        });
        
        // Save as suggestion (all ISO codes)
        setSuggestedCity(detectedCity);
        setSuggestedState(detectedState);
        setSuggestedCountry(detectedCountry);
        
        // If forceUpdate or no values selected, fill the fields
        if (forceUpdate || (!selectedCity && !selectedState)) {
          setSelectedCountry(detectedCountry);
          setSelectedState(detectedState);
          setTimeout(() => {
            setSelectedCity(detectedCity);
          }, 100);
          setSuggestionAccepted(true);
        }
        
        // Save coordinates to context
        if (locationData.latitude && locationData.longitude) {
          updateData({
            geolocation: { 
              latitude: locationData.latitude, 
              longitude: locationData.longitude 
            }
          });
        }
      },
      onPermissionDenied: () => {
        debugLogger.warn('GeolocationStep: Permission denied - user can still enter manually');
      },
      onError: (error) => {
        debugLogger.warn('GeolocationStep: Auto-detection failed, user can enter manually:', error);
      }
    });
  };

  const handleAcceptSuggestion = () => {
    // Update ALL fields including country
    setSelectedCountry(suggestedCountry || 'BR');
    setSelectedState(suggestedState);
    setSelectedCity(suggestedCity);
    setSuggestionAccepted(true);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedState('');
    setSelectedCity('');
    setSuggestionAccepted(false);
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value);
    setSelectedCity('');
    setSuggestionAccepted(false);
  };

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    setSuggestionAccepted(false);
  };

  const handleNext = async () => {
    // Validate city is filled
    if (!selectedCity.trim()) {
      setErrorDetails({
        message: 'Por favor, informe a cidade onde você mora.',
        timestamp: new Date().toISOString(),
        step: 6,
        type: 'city_missing'
      });
      setShowErrorModal(true);
      return;
    }

    // Update context with location data
    const locationUpdate: any = {
      location: {
        city: selectedCity.trim(),
        state: selectedState,
        country: selectedCountry,
      }
    };

    // Keep coordinates if they exist
    if (data?.geolocation?.latitude && data?.geolocation?.longitude) {
      locationUpdate.geolocation = data.geolocation;
    }

    updateData(locationUpdate);

    // Save and advance
    const success = await validateAndSaveStep(
      { ...data, ...locationUpdate },
      {
        stepNumber: 6,
        customValidation: (stepData) => {
          if (!stepData.location?.city) {
            return { isValid: false, error: 'Cidade é obrigatória' };
          }
          return { isValid: true };
        },
        onSuccess: () => {
          debugLogger.debug('GeolocationStep: Location step completed successfully');
          localStorage.removeItem('temp_location_obtained');
          nextStep();
        },
        onError: (error, details) => {
          debugLogger.error('GeolocationStep: Step failed:', error);
          setErrorDetails({
            message: error || 'Erro desconhecido',
            timestamp: details?.timestamp || new Date().toISOString(),
            step: details?.step || 6,
            type: details?.type || 'location_save_failed'
          });
          setShowErrorModal(true);
        }
      }
    );

    if (!success) {
      debugLogger.error('GeolocationStep: Validation failed');
    }
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  const handleCloseModal = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  // Can continue if city is filled
  const canContinue = selectedCity.trim().length > 0;

  return (
    <>
      <StickyOnboardingFooter
        buttonText="Continuar"
        onClick={handleNext}
        disabled={!canContinue}
        isLoading={isValidating}
        loadingText="Salvando..."
      >
        <div className="max-w-2xl mx-auto">
          {/* Title and description */}
          <div className="text-center mb-4 md:mb-8">
            <div className="w-14 h-14 md:w-20 md:h-20 bg-luxury-pinkBright/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <MapPin className="h-7 w-7 md:h-10 md:w-10 text-luxury-pinkBright" />
            </div>
            
            <h1 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
              Onde você mora hoje?
            </h1>
            <p className="text-xs md:text-sm text-white/70 max-w-md mx-auto leading-relaxed">
              Escolha a cidade onde você realmente mora.
            </p>
          </div>

          {/* Automatic suggestion banner */}
          {(suggestedCity || suggestedState) && !suggestionAccepted && (
            <div className="mb-4 md:mb-6">
              <LocationSuggestionBanner
                suggestedCity={suggestedCity}
                suggestedState={suggestedState}
                suggestedCountry={suggestedCountry}
                onAccept={handleAcceptSuggestion}
                isAccepted={suggestionAccepted}
              />
            </div>
          )}

          {/* Loading auto-detection */}
          {isGettingLocation && !selectedCity && !selectedState && (
            <div className="flex items-center justify-center gap-3 mb-4 md:mb-6 text-white/60">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Detectando sua localização...</span>
            </div>
          )}

          {/* Manual selection form */}
          <div className="space-y-4 md:space-y-5 bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
            <div className="space-y-1.5 md:space-y-2">
              <label className="text-xs md:text-sm font-medium text-white/80">
                País
              </label>
              <CountrySelect
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Selecione seu país"
              />
            </div>

            <div className="space-y-1.5 md:space-y-2">
              <label className="text-xs md:text-sm font-medium text-white/80">
                Estado / Província
              </label>
              <StateSelect
                value={selectedState}
                onChange={handleStateChange}
                countryCode={selectedCountry}
                placeholder="Selecione o estado"
              />
            </div>

            <div className="space-y-1.5 md:space-y-2">
              <label className="text-xs md:text-sm font-medium text-white/80">
                Cidade
              </label>
              <CitySelect
                value={selectedCity}
                onChange={handleCityChange}
                countryCode={selectedCountry}
                stateCode={selectedState}
                placeholder="Selecione sua cidade"
              />
            </div>

            {/* Button to retry detection */}
            {!isGettingLocation && (
              <Button
                onClick={() => handleAutoDetect(true)}
                variant="ghost"
                size="sm"
                className="w-full text-white/50 hover:text-white/70 hover:bg-white/5"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Usar minha localização
              </Button>
            )}
          </div>

          {/* Privacy info */}
          <div className="mt-4 md:mt-6 p-3 md:p-4 bg-white/5 border border-white/10 rounded-lg">
            <p className="text-[10px] md:text-xs text-white/50 text-center">
              🔒 Sua cidade é usada para sugerir perfis próximos. 
              Você pode alterar nas configurações.
            </p>
          </div>
        </div>
      </StickyOnboardingFooter>

      {showErrorModal && errorDetails && (
        <OnboardingErrorModal
          isOpen={showErrorModal}
          onClose={handleCloseModal}
          onRetry={handleRetry}
          stepNumber={6}
          errorDetails={errorDetails}
        />
      )}
    </>
  );
};

export default GeolocationStep;
